import React, { useEffect, useState } from 'react';
import {
  Box,
  Snackbar,
  Alert,
  AlertTitle,
  IconButton,
  Collapse,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Badge,
  Tooltip,
  Fade,
  Slide
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import { useRealtime } from '../contexts/RealtimeContext';

interface NotificationItem {
  id: number;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  timestamp: string;
  data?: any;
}

interface AlertItem {
  id: number;
  severity: 'error' | 'warning' | 'info' | 'success';
  message: string;
  timestamp: string;
  data?: any;
}

interface SystemMessageItem {
  id: number;
  message: string;
  type: string;
  timestamp: string;
  data?: any;
}

const RealtimeNotifications: React.FC = () => {
  const {
    notifications,
    alerts,
    systemMessages,
    clearNotifications,
    clearAlerts,
    clearSystemMessages
  } = useRealtime();

  const [expandedNotifications, setExpandedNotifications] = useState(false);
  const [expandedAlerts, setExpandedAlerts] = useState(false);
  const [expandedSystemMessages, setExpandedSystemMessages] = useState(false);
  const [autoHideDuration, setAutoHideDuration] = useState(6000);

  // Auto-hide notifications after 6 seconds
  useEffect(() => {
    if (notifications.length > 0) {
      const timer = setTimeout(() => {
        clearNotifications();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [notifications, autoHideDuration, clearNotifications]);

  // Auto-hide alerts after 10 seconds
  useEffect(() => {
    if (alerts.length > 0) {
      const timer = setTimeout(() => {
        clearAlerts();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [alerts, clearAlerts]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, maxWidth: 400 }}>
      {/* Notifications */}
      {notifications.length > 0 && (
        <Fade in={true}>
          <Paper
            elevation={8}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={notifications.length} color="secondary">
                  <NotificationsIcon />
                </Badge>
                <Typography variant="subtitle2" fontWeight="bold">
                  Bildirimler
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Genişlet/Daralt">
                  <IconButton
                    size="small"
                    onClick={() => setExpandedNotifications(!expandedNotifications)}
                    sx={{ color: 'inherit' }}
                  >
                    {expandedNotifications ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tümünü Temizle">
                  <IconButton
                    size="small"
                    onClick={clearNotifications}
                    sx={{ color: 'inherit' }}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Collapse in={expandedNotifications}>
              <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
                {notifications.map((notification: NotificationItem, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem sx={{ py: 1, px: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getNotificationIcon(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {notification.message}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </Paper>
        </Fade>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <Fade in={true}>
          <Paper
            elevation={8}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'error.main',
                color: 'error.contrastText'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={alerts.length} color="secondary">
                  <WarningIcon />
                </Badge>
                <Typography variant="subtitle2" fontWeight="bold">
                  Uyarılar
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Genişlet/Daralt">
                  <IconButton
                    size="small"
                    onClick={() => setExpandedAlerts(!expandedAlerts)}
                    sx={{ color: 'inherit' }}
                  >
                    {expandedAlerts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tümünü Temizle">
                  <IconButton
                    size="small"
                    onClick={clearAlerts}
                    sx={{ color: 'inherit' }}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Collapse in={expandedAlerts}>
              <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
                {alerts.map((alert: AlertItem, index) => (
                  <React.Fragment key={alert.id}>
                    <ListItem sx={{ py: 1, px: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getAlertIcon(alert.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {alert.message}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(alert.timestamp)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < alerts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </Paper>
        </Fade>
      )}

      {/* System Messages */}
      {systemMessages.length > 0 && (
        <Fade in={true}>
          <Paper
            elevation={8}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'info.main',
                color: 'info.contrastText'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={systemMessages.length} color="secondary">
                  <InfoIcon />
                </Badge>
                <Typography variant="subtitle2" fontWeight="bold">
                  Sistem Mesajları
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Genişlet/Daralt">
                  <IconButton
                    size="small"
                    onClick={() => setExpandedSystemMessages(!expandedSystemMessages)}
                    sx={{ color: 'inherit' }}
                  >
                    {expandedSystemMessages ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tümünü Temizle">
                  <IconButton
                    size="small"
                    onClick={clearSystemMessages}
                    sx={{ color: 'inherit' }}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Collapse in={expandedSystemMessages}>
              <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
                {systemMessages.map((message: SystemMessageItem, index) => (
                  <React.Fragment key={message.id}>
                    <ListItem sx={{ py: 1, px: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {message.message}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(message.timestamp)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < systemMessages.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default RealtimeNotifications;


